import type { DokumentDetail, DokumentTyp } from "@/lib/dokumente";
import {
  getPositionMeta,
  linkStrukturTitelLocalIds,
  newLocalId,
  patchPositionMeta,
  toApiPosition,
  toEditorPosition,
  type EditorPosition,
} from "@/pages/dokumente/documentPositionModel";

export interface GdTemplatePosition {
  template_local_id: string;
  position_typ: string;
  artikel_id?: number | null;
  ist_freier_artikel?: boolean;
  bezeichnung: string;
  beschreibung?: string | null;
  hero_nr?: string | null;
  einheit?: string | null;
  menge: number;
  einzelpreis_netto: number;
  rabatt_prozent: number;
  rabatt_betrag?: number;
  rabatt_modus?: "prozent" | "betrag";
  rabatt_ausweis?: "intern" | "dokument";
  steuersatz_prozent: number;
  sort_index: number;
  kalkulation_json?: unknown;
}

export interface GdTemplateVersionSummary {
  id: number;
  version: number;
  gueltig_ab?: string | null;
  name?: string | null;
  position_count?: number;
}

export interface GdDocumentTemplateDetail {
  id: number;
  name: string;
  beschreibung?: string | null;
  dokument_typ: DokumentTyp;
  betreff?: string | null;
  einleitung?: string | null;
  schlusstext?: string | null;
  positionen: GdTemplatePosition[];
  position_count: number;
  sort_index: number;
  schlagworte?: string[];
  ordner_ids?: number[];
  sordner_ids?: number[];
  familie_id?: number;
  version?: number;
  gueltig_ab?: string | null;
  versionen?: GdTemplateVersionSummary[];
  naechste_version?: GdTemplateVersionSummary | null;
  matched_ordner?: boolean;
  created_by?: string;
  created_at?: string;
  updated_at?: string;
}

export interface GdDocumentTemplateListItem {
  id: number;
  name: string;
  beschreibung?: string | null;
  dokument_typ: DokumentTyp;
  betreff?: string | null;
  position_count: number;
  sort_index: number;
  schlagworte?: string[];
  ordner_ids?: number[];
  sordner_ids?: number[];
  familie_id?: number;
  version?: number;
  gueltig_ab?: string | null;
  naechste_version?: GdTemplateVersionSummary | null;
  matched_ordner?: boolean;
  created_by?: string;
  created_at?: string;
  updated_at?: string;
}

export interface SaveDocumentTemplatePayload {
  name: string;
  beschreibung?: string | null;
  dokument_typ: DokumentTyp;
  includeHeaderTexts: boolean;
  betreff?: string | null;
  einleitung?: string | null;
  schlusstext?: string | null;
  positionen: GdTemplatePosition[];
  schlagworte?: string[];
  ordner_ids?: number[];
  sordner_ids?: number[];
}

export function editorPositionsToTemplatePositions(positions: EditorPosition[]): GdTemplatePosition[] {
  return positions.map((row, index) => {
    const api = toApiPosition(row, index, positions);
    return {
      template_local_id: row.localId,
      position_typ: api.position_typ,
      artikel_id: api.artikel_id,
      ist_freier_artikel: row.ist_freier_artikel,
      bezeichnung: api.bezeichnung,
      beschreibung: api.beschreibung,
      hero_nr: api.hero_nr,
      einheit: api.einheit,
      menge: api.menge,
      einzelpreis_netto: api.einzelpreis_netto,
      rabatt_prozent: api.rabatt_prozent,
      rabatt_betrag: api.rabatt_betrag,
      rabatt_modus: api.rabatt_modus,
      rabatt_ausweis: api.rabatt_ausweis,
      steuersatz_prozent: api.steuersatz_prozent,
      sort_index: index,
      kalkulation_json: api.kalkulation_json,
    };
  });
}

function remapNestedLocalIds<T extends { localId?: string }>(
  items: T[] | undefined,
  idMap: Map<string, string>,
  prefix: string,
): T[] {
  if (!items?.length) return [];
  return items.map((item) => {
    const oldId = item.localId || "";
    const nextId = idMap.get(oldId) || newLocalId(prefix);
    idMap.set(oldId, nextId);
    return { ...item, localId: nextId };
  });
}

export function templatePositionsToEditorPositions(items: GdTemplatePosition[]): EditorPosition[] {
  const idMap = new Map<string, string>();
  for (const item of items) {
    idMap.set(item.template_local_id, newLocalId());
  }

  const raw = items.map((item, index) => {
    const editor = toEditorPosition(
      {
        position_typ: item.position_typ as EditorPosition["position_typ"],
        artikel_id: item.artikel_id,
        ist_freier_artikel: item.ist_freier_artikel,
        bezeichnung: item.bezeichnung,
        beschreibung: item.beschreibung,
        hero_nr: item.hero_nr,
        einheit: item.einheit,
        menge: item.menge,
        einzelpreis_netto: item.einzelpreis_netto,
        rabatt_prozent: item.rabatt_prozent,
        rabatt_betrag: item.rabatt_betrag,
        rabatt_modus: item.rabatt_modus,
        rabatt_ausweis: item.rabatt_ausweis,
        steuersatz_prozent: item.steuersatz_prozent,
        sort_index: index,
        kalkulation_json: item.kalkulation_json as EditorPosition["kalkulation_json"],
      },
      index,
      idMap.get(item.template_local_id),
    );

    const meta = getPositionMeta(editor);
    let next = editor;
    if (meta.struktur_titel_local_id) {
      const mappedTitel = idMap.get(meta.struktur_titel_local_id);
      if (mappedTitel) {
        next = patchPositionMeta(next, {
          struktur_titel_local_id: mappedTitel,
          struktur_titel_position_id: null,
        });
      }
    }

    if (next.kalkulation_json && typeof next.kalkulation_json === "object" && "artikel_liste" in next.kalkulation_json) {
      const kalk = next.kalkulation_json as {
        artikel_liste?: Array<{ localId?: string }>;
        lohn_liste?: Array<{ localId?: string }>;
      };
      next = {
        ...next,
        kalkulation_json: {
          ...kalk,
          artikel_liste: remapNestedLocalIds(kalk.artikel_liste, idMap, "art"),
          lohn_liste: remapNestedLocalIds(kalk.lohn_liste, idMap, "lohn"),
        } as EditorPosition["kalkulation_json"],
      };
    }

    return next;
  });

  return linkStrukturTitelLocalIds(raw);
}

export function appendTemplatePositions(
  existing: EditorPosition[],
  templatePositions: GdTemplatePosition[],
  insertAt?: number,
): EditorPosition[] {
  const cloned = templatePositionsToEditorPositions(templatePositions);
  if (insertAt == null || !Number.isFinite(insertAt)) {
    return [...existing, ...cloned].map((row, index) => ({ ...row, sort_index: index }));
  }
  const clamped = Math.max(0, Math.min(Math.floor(insertAt), existing.length));
  return [...existing.slice(0, clamped), ...cloned, ...existing.slice(clamped)].map((row, index) => ({
    ...row,
    sort_index: index,
  }));
}

export function buildSaveTemplatePayload(
  doc: DokumentDetail,
  positions: EditorPosition[],
  options: {
    name: string;
    beschreibung?: string;
    includeHeaderTexts: boolean;
    schlagworte?: string[];
    ordner_ids?: number[];
    sordner_ids?: number[];
  },
): SaveDocumentTemplatePayload {
  return {
    name: options.name.trim(),
    beschreibung: options.beschreibung?.trim() || null,
    dokument_typ: doc.dokument_typ,
    includeHeaderTexts: options.includeHeaderTexts,
    betreff: options.includeHeaderTexts ? doc.betreff ?? null : null,
    einleitung: options.includeHeaderTexts ? doc.einleitung ?? null : null,
    schlusstext: options.includeHeaderTexts ? doc.schlusstext ?? null : null,
    positionen: editorPositionsToTemplatePositions(positions),
    schlagworte: options.schlagworte ?? [],
    ordner_ids: options.ordner_ids ?? [],
    sordner_ids: options.sordner_ids ?? [],
  };
}

export interface ApplyDocumentTemplateResult {
  positions: EditorPosition[];
  headerPatch?: Partial<DokumentDetail>;
}

export function applyDocumentTemplate(
  existing: EditorPosition[],
  template: GdDocumentTemplateDetail,
  mode: "append" | "replace",
  applyHeaderTexts: boolean,
  insertAt?: number,
): ApplyDocumentTemplateResult {
  const positions =
    mode === "replace"
      ? templatePositionsToEditorPositions(template.positionen)
      : appendTemplatePositions(existing, template.positionen, insertAt);

  const headerPatch =
    applyHeaderTexts
      ? {
          ...(template.betreff ? { betreff: template.betreff } : {}),
          ...(template.einleitung ? { einleitung: template.einleitung } : {}),
          ...(template.schlusstext ? { schlusstext: template.schlusstext } : {}),
        }
      : undefined;

  return { positions, headerPatch };
}

export function filterGdTemplates(
  items: GdDocumentTemplateListItem[],
  query: string,
): GdDocumentTemplateListItem[] {
  const q = query.trim().toLowerCase();
  if (!q) return items;
  return items.filter((item) => {
    const haystack = [
      item.name,
      item.beschreibung || "",
      item.betreff || "",
      ...(item.schlagworte || []),
    ]
      .join(" ")
      .toLowerCase();
    return haystack.includes(q);
  });
}
